import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import colors from "../../assets/theme/colors";

const pageColors = {
  ink: "#17251f",
  muted: "#52645c",
  sage: colors.semantic.sageDeep,
  sageSoft: "#e8f4ef",
  border: "#dfe8e3",
};

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      color: pageColors.sage,
      fontSize: "0.82rem",
      fontWeight: 850,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
    }}
  >
    {children}
  </Typography>
);

const Bullets = ({ items }) => (
  <Box component="ul" sx={{ m: 0, pl: 2.2, color: pageColors.muted }}>
    {items.map((item) => (
      <Typography
        key={item}
        component="li"
        sx={{
          pl: 0.5,
          mb: 0.65,
          fontSize: { xs: "0.95rem", md: "1rem" },
          lineHeight: 1.45,
          "&::marker": {
            color: "rgba(126, 184, 164, 0.95)",
          },
        }}
      >
        {item}
      </Typography>
    ))}
  </Box>
);

const TabletSummary = ({ summary }) => (
  <Box
    sx={{
      position: "relative",
      p: { xs: 1.2, md: 1.8 },
      pb: { xs: 2.1, md: 2.6 },
      borderRadius: { xs: 5, md: 7 },
      bgcolor: "#141c19",
      border: "1px solid rgba(255, 255, 255, 0.08)",
      boxShadow: "0 30px 70px rgba(20, 28, 25, 0.22)",
      "&::after": {
        content: '""',
        position: "absolute",
        left: "50%",
        bottom: { xs: 7, md: 9 },
        width: { xs: 34, md: 42 },
        height: 4,
        borderRadius: 999,
        bgcolor: "rgba(255, 255, 255, 0.42)",
        transform: "translateX(-50%)",
      },
    }}
  >
    <Stack spacing={1.2}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: "rgba(255, 255, 255, 0.5)",
          mx: "auto",
          boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.16)",
        }}
      />
      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: { xs: 4, md: 5 },
          bgcolor: "#fbfaf7",
          border: `1px solid ${pageColors.border}`,
        }}
      >
        <Stack spacing={2.4}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2}>
            <Box>
              <SectionLabel>{summary.label}</SectionLabel>
              <Typography sx={{ mt: 1, color: pageColors.ink, fontSize: { xs: "1.65rem", md: "2rem" }, fontWeight: 900 }}>
                {summary.title}
              </Typography>
            </Box>
            <Typography sx={{ color: pageColors.muted, fontWeight: 750 }}>{summary.date}</Typography>
          </Stack>

          <Box sx={{ height: 1, bgcolor: pageColors.border }} />

          <Stack spacing={1.1}>
            <SectionLabel>{summary.primaryHeading}</SectionLabel>
            <Bullets items={summary.primaryItems} />
          </Stack>

          <Stack spacing={1.1}>
            <SectionLabel>{summary.secondaryHeading}</SectionLabel>
            <Bullets items={summary.secondaryItems} />
          </Stack>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              p: 1.4,
              borderRadius: 3,
              bgcolor: pageColors.sage,
              color: "#fff",
              fontWeight: 900,
            }}
          >
            <CheckBoxOutlinedIcon fontSize="small" />
            {summary.badge}
          </Box>
          <Typography sx={{ textAlign: "center", color: pageColors.muted, fontSize: "0.85rem" }}>
            {summary.caption}
          </Typography>
        </Stack>
      </Box>
    </Stack>
  </Box>
);

const ProviderFeatureCards = ({ cards }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: { xs: 2.5, md: 3 } }}>
    {cards.map(({ title, body, Icon }) => (
      <Box key={title} sx={{ p: { xs: 3, md: 4 }, border: `1px solid ${pageColors.border}`, borderRadius: 4, bgcolor: "#fff" }}>
        <Stack spacing={2}>
          <Box sx={{ width: 56, height: 56, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: pageColors.sageSoft, color: pageColors.sage }}>
            <Icon />
          </Box>
          <Typography sx={{ color: pageColors.ink, fontWeight: 900, fontSize: "1.25rem", lineHeight: 1.2 }}>{title}</Typography>
          <Typography sx={{ color: pageColors.muted, fontSize: "1.05rem", lineHeight: 1.6 }}>{body}</Typography>
        </Stack>
      </Box>
    ))}
  </Box>
);

const AudienceProviderSection = ({ provider }) => (
  <Stack id="care-provider" spacing={{ xs: 4, md: 5 }} sx={{ scrollMarginTop: 96, borderTop: `1px solid ${pageColors.border}`, pt: { xs: 5, md: 7 } }}>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "0.95fr 1.05fr" }, gap: { xs: 4, lg: 7 }, alignItems: "center" }}>
      <Box>
        <SectionLabel>{provider.eyebrow}</SectionLabel>
        <Typography sx={{ mt: 1.5, maxWidth: 720, color: pageColors.ink, fontSize: { xs: "2rem", md: "3.1rem" }, fontWeight: 850, lineHeight: 1.08 }}>
          {provider.title}
        </Typography>
        <Typography sx={{ mt: 2.4, maxWidth: 680, color: pageColors.muted, fontSize: { xs: "1.08rem", md: "1.35rem" }, lineHeight: 1.55 }}>
          {provider.body}
        </Typography>
      </Box>
      <TabletSummary summary={provider.summary} />
    </Box>

    <ProviderFeatureCards cards={provider.features} />

    <Box>
      <SectionLabel>{provider.stepsEyebrow}</SectionLabel>
      <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: { xs: 2.5, md: 3 } }}>
        {provider.steps.map(([title, body], index) => (
          <Stack key={title} spacing={1.5}>
            <Box sx={{ width: 38, height: 38, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: pageColors.sageSoft, color: pageColors.sage, fontWeight: 900 }}>
              {index + 1}
            </Box>
            <Typography sx={{ color: pageColors.ink, fontWeight: 900, fontSize: "1.12rem" }}>{title}</Typography>
            <Typography sx={{ color: pageColors.muted, lineHeight: 1.55 }}>{body}</Typography>
          </Stack>
        ))}
      </Box>
    </Box>

    <Button href="/login" endIcon={<ChevronRightRoundedIcon />} variant="contained" sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, borderRadius: "999px", px: 4, py: 1.4, textTransform: "none", fontWeight: 900, bgcolor: pageColors.sage, boxShadow: "0 18px 38px rgba(111, 165, 148, 0.22)", "&:hover": { bgcolor: pageColors.sage, boxShadow: "0 18px 38px rgba(111, 165, 148, 0.22)" } }}>
      {provider.cta}
    </Button>
  </Stack>
);

AudienceProviderSection.defaultProps = {
  provider: {
    features: [],
    steps: [],
    summary: {
      primaryItems: [],
      secondaryItems: [],
    },
  },
};

export default AudienceProviderSection;
